import { DOCUMENT } from '@angular/common';
import { effect, inject, Injectable, OnDestroy } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';
import { AuthService } from './auth.service';
import { WINDOW } from '../models/window.token';
import { LiveAssistantStateService } from '../../features/live-assistant/services/live-assistant-state.service';
import { PipService } from '../../shared/services/pip.service';

export const INACTIVITY_LOGOUT_MS = 24 * 60 * 60 * 1000;
const ACTIVITY_THROTTLE_MS = 1000;

const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart'] as const;

@Injectable({
  providedIn: 'root',
})
export class InactivityLogoutService implements OnDestroy {
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);
  private readonly window = inject(WINDOW);
  private readonly document = inject(DOCUMENT);
  private readonly liveAssistantState = inject(LiveAssistantStateService);
  private readonly pipService = inject(PipService);

  private timeoutId: ReturnType<typeof setTimeout> | null = null;
  private throttleTimer: ReturnType<typeof setTimeout> | null = null;
  private started = false;
  private lastActivityAt = 0;
  private wasTrackingInactivity = false;

  constructor() {
    effect(() => {
      const sessionState = this.liveAssistantState.sessionState();
      const isMaster = this.pipService.isMaster();

      if (!this.started) {
        return;
      }

      if (sessionState === 'active' || !isMaster) {
        this.clearTimer();
        this.wasTrackingInactivity = false;
        return;
      }

      this.syncTimer();
    });

    this.router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe(() => {
      if (this.started) {
        this.syncTimer();
      }
    });
  }

  start(): void {
    if (this.started || !this.window) {
      return;
    }

    const win = this.window;
    this.started = true;
    this.lastActivityAt = Date.now();

    for (const eventName of ACTIVITY_EVENTS) {
      win.addEventListener(eventName, this.handleActivity, { passive: true });
    }

    this.document.addEventListener('visibilitychange', this.handleVisibilityChange);
    this.syncTimer();
  }

  private readonly handleActivity = (): void => {
    this.lastActivityAt = Date.now();
    this.clearTimer();

    if (this.throttleTimer !== null) {
      return;
    }

    this.throttleTimer = setTimeout(() => {
      this.throttleTimer = null;
      this.syncTimer();
    }, ACTIVITY_THROTTLE_MS);
  };

  private readonly handleVisibilityChange = (): void => {
    if (!this.document.hidden) {
      this.syncTimer();
    }
  };

  private syncTimer(): void {
    if (!this.shouldTrackInactivity()) {
      this.clearTimer();
      this.wasTrackingInactivity = false;
      return;
    }

    const now = Date.now();
    if (!this.wasTrackingInactivity || this.lastActivityAt === 0) {
      this.lastActivityAt = now;
    }
    this.wasTrackingInactivity = true;

    const remainingMs = INACTIVITY_LOGOUT_MS - (now - this.lastActivityAt);
    if (remainingMs <= 0) {
      this.triggerLogout();
      return;
    }

    this.clearTimer();
    this.timeoutId = setTimeout(() => {
      if (!this.shouldTrackInactivity()) {
        return;
      }

      this.triggerLogout();
    }, remainingMs);
  }

  private shouldTrackInactivity(): boolean {
    return !!this.authService.getSession() && this.pipService.isMaster() && this.liveAssistantState.sessionState() !== 'active';
  }

  private clearTimer(): void {
    if (this.timeoutId !== null) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
  }

  private triggerLogout(): void {
    this.authService.logout();
    this.clearTimer();
    this.wasTrackingInactivity = false;
    void this.router.navigate(['/login']);
  }

  ngOnDestroy(): void {
    this.clearTimer();
    if (this.throttleTimer !== null) {
      clearTimeout(this.throttleTimer);
      this.throttleTimer = null;
    }

    if (this.window) {
      const win = this.window;
      for (const eventName of ACTIVITY_EVENTS) {
        win.removeEventListener(eventName, this.handleActivity);
      }
    }

    this.document.removeEventListener('visibilitychange', this.handleVisibilityChange);
  }
}
